import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Table } from '../../components/ui/Table';
import { syncTimestampColumns } from '../../components/ui/SyncTimestamps';
import { Badge } from '../../components/ui/Badge';
import { FilterBar } from '../../components/ui/FilterBar';
import api from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';
import { ProductsEmptyState } from './components/ProductsEmptyState';

const PER_PAGE = 10;

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const { user, hasPermission, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !hasPermission('view_products')) {
      navigate('/dashboard', { replace: true });
    }
  }, [authLoading, user, hasPermission, navigate]);

  const [products, setProducts] = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [page, setPage]         = useState(1);

  const loadProducts = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: String(PER_PAGE) });
    if (filter !== 'all') params.set('status', filter);
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    api.get(`/products?${params}`)
      .then((res) => { setProducts(res.data.data ?? []); setTotal(res.data.total ?? 0); })
      .catch((e) => setError(e.message || t('common.error_occurred')))
      .finally(() => setLoading(false));
  }, [filter, debouncedSearch, page, t]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const columns = useMemo(() => [
    {
      key: 'erpCode',
      label: t('products.col_erp_code'),
      render: (v) => <span className="font-mono text-[12px] font-medium text-primary">{v || '—'}</span>,
    },
    {
      key: 'name',
      label: t('products.col_name'),
      render: (v) => <span className="font-medium text-slate-700">{v || '—'}</span>,
    },
    {
      key: 'syncro',
      label: t('products.col_syncro'),
      render: (v) => <span className="font-mono text-[12px] text-slate-500">{v || '—'}</span>,
    },
    {
      key: 'varietyName',
      label: t('products.col_variety'),
      render: (v, row) => (
        <span className="text-slate-600">
          {v || '—'}{row.varietyNo ? <span className="text-slate-400"> · {row.varietyNo}</span> : null}
        </span>
      ),
    },
    {
      key: 'isActive',
      label: t('products.col_status'),
      render: (v) => (
        <Badge variant={v ? 'success' : 'info'}>
          {v ? t('products.status_active') : t('products.status_inactive')}
        </Badge>
      ),
    },
    ...syncTimestampColumns(t, i18n.language),
  ], [t, i18n.language]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">{t('products.title')}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{t('products.subtitle')}</p>
        </div>
        <button
          onClick={loadProducts}
          disabled={loading}
          className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition"
        >
          <RefreshCw size={13} strokeWidth={1.75} className={loading ? 'animate-spin' : ''} />
          {t('common.refresh')}
        </button>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-center justify-between text-[13px] text-rose-700">
          <span>{error}</span>
          <button onClick={loadProducts} className="font-medium underline text-[12px] text-rose-700">
            {t('common.retry')}
          </button>
        </div>
      )}

      <FilterBar
        groups={[{
          value: filter,
          onChange: (key) => { setFilter(key); setPage(1); },
          tabs: [
            { key: 'all',      label: t('products.filter_all') },
            { key: 'active',   label: t('products.filter_active') },
            { key: 'inactive', label: t('products.filter_inactive') },
          ],
        }]}
        search={search}
        onSearch={(e) => { setSearch(e.target.value); setPage(1); }}
        searchPlaceholder={t('products.search_placeholder')}
      />

      <Table
        columns={columns}
        rows={products}
        rowKey="_id"
        loading={loading}
        emptyState={<ProductsEmptyState />}
        page={page}
        total={total}
        perPage={PER_PAGE}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />
    </div>
  );
}
